import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const THEMES = [
  ["Vulnerability handling", "Documented intake, triage and time-bound response for the whole support period, plus a monitored public reporting contact."],
  ["Secure by default", "The shipped configuration should not depend on the integrator to remove default credentials or disable debug interfaces."],
  ["Secure update capability", "Field-updatable firmware with signature verification and rollback protection, and a defined delivery route to users."],
  ["SBOM and components", "A maintained, machine-readable component inventory per release so newly disclosed vulnerabilities can be triaged against shipped firmware."],
  ["Support period", "A published support period with security fixes provided free of charge, plus end-of-support handling for users."],
  ["Security documentation", "Hardening and secure commissioning guidance the integrator can actually follow."],
];

export default function CraReadiness() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="blueprint border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-24">
          <p className="overline text-navy-800">EU Cyber Resilience Act</p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-navy-900 sm:text-5xl">
            CRA readiness for products with digital elements
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            If you place connected products on the EU market, expectations now extend well past the
            release date: vulnerability handling, secure update delivery, component transparency and a
            declared support period. This assessment gives you an indicative view of where your
            product stands and what evidence you would need to show.
          </p>
          <Button
            size="lg"
            className="mt-9 bg-navy-800 text-white hover:bg-navy-700"
            onClick={() => navigate("/register")}
            data-testid="cra-start-button"
          >
            Start a CRA readiness check <ArrowRight size={17} className="ml-2" />
          </Button>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-navy-900">What we assess</h2>
        <div className="mt-8 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
          {THEMES.map(([title, body]) => (
            <div key={title} className="bg-white p-7">
              <CheckCircle size={20} weight="duotone" className="text-navy-800" />
              <h3 className="mt-4 font-display text-base font-bold text-navy-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 border border-slate-200 bg-slate-100/70 p-6 text-sm leading-relaxed text-slate-600">
          Applicability is not assumed. If your product profile shows no EU market placement, CRA
          scoring is hidden and marked <span className="font-mono">Not Applicable Based on Current Product Profile</span>.
          If EU placement exists without network connectivity, the score is shown with a
          <span className="font-mono"> Limited Applicability</span> flag.
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
